import { useState, useEffect } from 'react';
import { isEmpty, get, uniq, intersection } from 'lodash';

import { fetchOffsetWellSelections, fetchWellhubWells, resyncIdasWells } from '../utils/apiCalls';
import {
  ALL_SECTION_KEY,
  ALL_SECTION_LABEL,
  WellSectionColors,
  RESYNC_TASK_RESPONSE,
  RESYNC_STATUS,
} from '../constants';

import { showSuccessNotification, showErrorNotification } from '~/utils';

export const useBicWells = (isWDUser, currentUser, assetId, wells, isManualWellsEnabled) => {
  const [wellSections, setWellSections] = useState(null);
  const [offsetWellsBySection, setOffsetWellsBySection] = useState(null);
  const [resyncStatus, setResyncStatus] = useState(RESYNC_STATUS.INITIAL);
  const [taScores, setTaScores] = useState([]);

  useEffect(() => {
    const fetchOffsetWellSelectionData = async () => {
      try {
        const data = assetId ? await fetchOffsetWellSelections(assetId) : [];
        const filters = !isEmpty(data) && JSON.parse(data[0].data.filters);
        const result = !isEmpty(data) && data[0].data.result && JSON.parse(data[0].data.result);
        const manualWellIds =
          isManualWellsEnabled &&
          !isEmpty(data) &&
          data[0].data.manual_wells &&
          JSON.parse(data[0].data.manual_wells);
        const wellIds = wells.map(well => well.id);
        const taScores =
          isManualWellsEnabled &&
          !isEmpty(data) &&
          data[0].data.ta_scores &&
          JSON.parse(data[0].data.ta_scores);

        const sections =
          filters?.filterTabs?.map((tab, index) => ({
            value: tab.value,
            label: tab.name,
            color: WellSectionColors[index],
          })) || [];

        let allWellIds = [assetId];
        let wellsData = {};

        if (filters && result) {
          wellsData = sections.reduce((acc, { value }) => {
            const sectionWellIds = result?.[value] || [];
            const addedWellIds = uniq(sectionWellIds.concat(assetId));
            allWellIds = allWellIds.concat(addedWellIds);
            return { ...acc, [value]: addedWellIds };
          }, {});
        }

        if (manualWellIds && manualWellIds.length) {
          allWellIds = allWellIds.concat(manualWellIds);
          setTaScores(
            manualWellIds.map((wellId, index) => ({
              wellId,
              taScore: taScores?.[index] ?? '',
            }))
          );
        }

        wellsData[ALL_SECTION_KEY] = uniq(allWellIds);

        // NOTE: Filter the removed offset
        wellsData = Object.keys(wellsData).reduce(
          (acc, key) => ({
            ...acc,
            [key]: intersection(wellsData[key], wellIds),
          }),
          {}
        );

        setWellSections([{ value: ALL_SECTION_KEY, label: ALL_SECTION_LABEL }, ...sections]);
        setOffsetWellsBySection(wellsData);
      } catch (err) {
        console.error('offsetwell selection fetch error: ', err);
        setWellSections([]);
        setOffsetWellsBySection([]);
      }
    };

    const fetchWellhubData = async () => {
      try {
        const record = await fetchWellhubWells(assetId);
        const offsetWells = get(record[0], 'data.offset_wells') || [];
        let offsetWellsBySection = {
          [ALL_SECTION_KEY]: uniq(offsetWells.map(({ id }) => id).concat(assetId)),
        };
        setWellSections([{ value: ALL_SECTION_KEY, label: ALL_SECTION_LABEL }]);

        // NOTE: Filter the removed offset wells
        const wellIds = wells.map(well => well.id);
        offsetWellsBySection = Object.keys(offsetWellsBySection).reduce(
          (acc, key) => ({ ...acc, [key]: intersection(offsetWellsBySection[key], wellIds) }),
          {}
        );
        setOffsetWellsBySection(offsetWellsBySection);
      } catch (err) {
        console.error('wellhub fetch error: ', err);
        setWellSections([]);
        setOffsetWellsBySection([]);
      }
    };

    if (
      assetId &&
      wells &&
      (resyncStatus === RESYNC_STATUS.INITIAL || resyncStatus === RESYNC_STATUS.SUCCEEDED)
    ) {
      if (isWDUser) {
        fetchOffsetWellSelectionData();
      } else {
        fetchWellhubData();
      }
    }
  }, [isWDUser, assetId, wells, resyncStatus]);

  useEffect(() => {
    if (resyncStatus !== RESYNC_STATUS.TRIGGERED) {
      return;
    }

    async function handleResyncWells() {
      setResyncStatus(RESYNC_STATUS.RUNNING);
      const response =
        assetId && currentUser && currentUser.id ? await resyncIdasWells(currentUser, assetId) : [];

      if (response?.state === RESYNC_TASK_RESPONSE.SUCCESS) {
        setResyncStatus(RESYNC_STATUS.SUCCEEDED);
        showSuccessNotification('Re-sync wells from IDAS was successful');
      } else if (response?.state === RESYNC_TASK_RESPONSE.FAILURE) {
        setResyncStatus(RESYNC_STATUS.FAILED);
        const error = response?.fail_reason || 'Re-sync wells from IDAS failed';
        showErrorNotification(error);
      }
    }

    handleResyncWells();
  }, [resyncStatus]);

  return [
    wellSections,
    setWellSections,
    offsetWellsBySection,
    setOffsetWellsBySection,
    resyncStatus,
    setResyncStatus,
    taScores,
  ];
};
